/**
 * Série recente do veículo selecionado.
 *
 * Fica numa store, e não dentro do painel de detalhe, porque **duas telas
 * precisam da mesma série**: o bloco de condução e a camada de via no mapa.
 * Buscar duas vezes seria pagar a mesma consulta em dobro e, pior, arriscar as
 * duas mostrarem janelas diferentes do mesmo veículo.
 */
import { create } from "zustand";
import { api } from "../lib/api.ts";
import type { TelemetryRow } from "../lib/schemas.ts";

/** Janela da série. 6 h cobre um turno e cabe no teto de 500 linhas da API. */
export const HISTORY_HOURS = 6;

interface HistoryState {
  imei: string | null;
  rows: TelemetryRow[];
  loading: boolean;
  error: string | null;
  load: (token: string, imei: string, signal?: AbortSignal) => Promise<void>;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  imei: null,
  rows: [],
  loading: false,
  error: null,

  load: async (token, imei, signal) => {
    // Mesmo veículo e já carregado: não recarrega. Trocar de aba e voltar não
    // deveria custar outra consulta de 500 linhas.
    if (get().imei === imei && get().rows.length > 0) return;
    set({ imei, rows: [], loading: true, error: null });
    try {
      const response = await api.recentTelemetry(token, imei, HISTORY_HOURS, signal);
      // A resposta vem em ordem decrescente (é o caminho barato no banco); a
      // tela quer do mais antigo para o mais novo.
      set({ imei, rows: [...response.rows].reverse(), loading: false });
    } catch {
      if (signal?.aborted) return;
      set({ loading: false, error: "Não foi possível carregar o histórico deste equipamento." });
    }
  },

  clear: () => set({ imei: null, rows: [], loading: false, error: null }),
}));
